import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';

export interface Contract {
  id: string;
  deal_id: string;
  contract_name: string;
  contract_type: string;
  status: 'draft' | 'sent' | 'viewed' | 'completed' | 'declined' | 'expired' | 'void';
  pandadoc_document_id?: string;
  pandadoc_status?: string;
  pandadoc_session_id?: string;
  pandadoc_view_url?: string;
  pandadoc_download_url?: string;
  suvsign_document_id?: string;
  suvsign_status?: string;
  signed_file_url?: string;
  signatories?: any[];
  sent_at?: string;
  sent_by?: string;
  viewed_at?: string;
  signed_at?: string;
  signed_by_contact_id?: string;
  completed_at?: string;
  declined_at?: string;
  declined_reason?: string;
  expires_at?: string;
  contract_url?: string;
  template_id?: string;
  notes?: string;
  metadata?: any;
  created_by?: string;
  created_at: string;
  updated_at: string;
  document_id?: string;
}

export function useContracts(dealId?: string) {
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { toast } = useToast();

  const loadContracts = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('deal_contracts')
        .select('*')
        .order('created_at', { ascending: false });

      if (dealId) {
        query = query.eq('deal_id', dealId);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;
      setContracts(data as Contract[] || []);
    } catch (err) {
      setError(err as Error);
      toast({
        variant: 'destructive',
        title: 'Error loading contracts',
        description: (err as Error).message,
      });
    } finally {
      setLoading(false);
    }
  };

  const createContract = async (contractData: any) => {
    try {
      const { data, error: insertError } = await supabase
        .from('deal_contracts')
        .insert([contractData])
        .select()
        .single();

      if (insertError) throw insertError;

      toast({
        title: 'Contract created',
        description: 'The contract has been created successfully.',
      });

      await loadContracts();
      return data;
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error creating contract',
        description: (err as Error).message,
      });
      throw err;
    }
  };

  const updateContract = async (id: string, updates: any) => {
    try {
      const { error: updateError } = await supabase
        .from('deal_contracts')
        .update(updates)
        .eq('id', id);

      if (updateError) throw updateError;

      toast({
        title: 'Contract updated',
        description: 'The contract has been updated successfully.',
      });

      await loadContracts();
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error updating contract',
        description: (err as Error).message,
      });
      throw err;
    }
  };

  const deleteContract = async (id: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('deal_contracts')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      toast({
        title: 'Contract deleted',
        description: 'The contract has been deleted successfully.',
      });

      await loadContracts();
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error deleting contract',
        description: (err as Error).message,
      });
      throw err;
    }
  };

  useEffect(() => {
    loadContracts();
  }, [dealId]);

  return {
    contracts,
    loading,
    error,
    loadContracts,
    createContract,
    updateContract,
    deleteContract,
  };
}
